import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Space, Button, InputNumber, Input, Table, message, Modal, Tooltip, Upload, Select, Collapse, Radio } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import request from '../../../../utils/request';
import { getInventoryUnitDetail } from '../../../../services/inventory';

type BoxRow = { box_no: number; volume_cc?: number; weight_g?: number };

type Props = {
  data?: any;
  metrics?: any;
  instructionDetail?: any;
  onChange?: (k: string, v: any) => void;
  onSubmit?: () => Promise<void> | void;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  taskId?: number | null;
  executionSessionId?: string | null;
};

const SingleDropshipPackRenderer: React.FC<Props> = ({ data, metrics, instructionDetail, onChange, instructionCodeFromParent, canOperate, taskId, executionSessionId }) => {
  const sanitizeNullableText = (v: any): string => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    const low = s.toLowerCase();
    if (low === 'none' || low === 'null' || low === 'undefined' || s === '-') return '';
    return s;
  };
  const normalizeText = (v: any): string => {
    const s = String(v ?? '').trim();
    if (!s) return '-';
    const lowered = s.toLowerCase();
    if (lowered === 'none' || lowered === 'null' || lowered === 'undefined') return '-';
    return s;
  };

  const mapPackingMethodToZh = (m: any): string => {
    const code = String(m || '').trim();
    if (!code) return '-';
    if (code === 'warehouse_free') return '仓库免费包材';
    if (code === 'warehouse_paid') return '仓库收费包材';
    if (code === 'customer_carton') return '客户自购包材(纸箱)';
    if (code === 'customer_non_carton') return '客户自购包材(非纸箱)';
    if (code === 'special_packing') return '特殊包装';
    return code;
  };

  const extractUnitContentItems = (row: any): Array<{ barcode: string; qty: number }> => {
    try {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const candidates: any[] = [
        snap?.content?.items,
        row?.content?.items,
        row?.target?.content?.items,
        row?.items,
      ].filter(Boolean);
      for (const cand of candidates) {
        const arr = Array.isArray(cand) ? cand : [];
        if (arr.length > 0) {
          return arr.map((it: any) => {
            const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            let barcode = '';
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              if (sc.length > 0) {
                const codes = sc.map((c: any) => String(c?.code || '')).filter(Boolean);
                if (codes.length > 0) barcode = codes.join('/');
              }
            } catch {}
            if (!barcode) {
              barcode = String(
                it?.product_system_code
                || it?.system_code
                || it?.product_code
                || '',
              );
            }
            return { barcode, qty };
          }).filter(x => x.barcode || x.qty);
        }
      }
    } catch {}
    return [];
  };

  const paperSizeLabel = (v: string) => {
    const s = String(v || '').trim().toLowerCase();
    if (s === '4x6' || s === '4x6_thermal') return '4×6英寸热敏纸打印';
    if (s === 'a4') return 'A4激光打印';
    return v || '-';
  };
  const placementLabel = (v: string) => {
    if (v === 'inside') return '放入包裹内';
    if (v === 'outside') return '贴在包裹外';
    return v || '-';
  };
  const getAttachmentPrintState = (a: any): 'print' | 'no_print' | 'unset' => {
    try {
      const pc = a?.meta?.print_config ?? a?.print_config;
      if (!pc || typeof pc !== 'object') return 'unset';
      if (pc.print === true) return 'print';
      if (pc.explicit_no_print === true) return 'no_print';
      return 'unset';
    } catch {
      return 'unset';
    }
  };
  const getAttachmentPrintText = (a: any): string => {
    try {
      const pc = a?.meta?.print_config ?? a?.print_config;
      const state = getAttachmentPrintState(a);
      if (state === 'print') {
        return `打印设置：${pc?.copies ?? '-'} 份 · ${paperSizeLabel(String(pc?.paper_size || ''))} · ${placementLabel(String(pc?.placement || ''))}`;
      }
      if (state === 'no_print') return '打印设置：明确无需打印';
      return '';
    } catch {
      return '';
    }
  };
  const hasPrintableRequirement = (a: any): boolean => {
    try {
      const label = String(a?.label || '').trim();
      if (label === '物流运单') return false;
      const state = getAttachmentPrintState(a);
      if (state === 'print') return true;
      if (state === 'no_print') return false;
      // 兼容历史数据：仅接受明确的“需要打印”布尔标记，避免把“未设置”误判为需打印。
      if (a?.print_required === true || a?.meta?.print_required === true) return true;
      return false;
    } catch {
      return false;
    }
  };

  const initialBoxes: BoxRow[] = useMemo(() => {
    try {
      const arr = Array.isArray(metrics?.boxes) ? metrics.boxes : [];
      return arr.map((b:any, i:number) => ({
        box_no: Number(b.box_no ?? (i + 1)),
        volume_cc: b.volume_cc != null ? Number(b.volume_cc) : undefined,
        weight_g: b.weight_g != null ? Number(b.weight_g) : undefined
      }));
    } catch { return []; }
  }, [metrics]);
  const [boxes] = useState<BoxRow[]>(initialBoxes);
  const saveTimer = useRef<any>(null);

  useEffect(() => {
    if (!onChange) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try { onChange('boxes', (boxes || []).map(b => ({ box_no: b.box_no, volume_cc: b.volume_cc, weight_g: b.weight_g, content: [] }))); } catch {}
    }, 300);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [boxes, onChange]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('附件预览');
  const [previewType, setPreviewType] = useState<'image' | 'file'>('file');
  const [previewPrintText, setPreviewPrintText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [unitContentCache, setUnitContentCache] = useState<Record<string, Array<{ barcode: string; qty: number }>>>({});
  const [packDataModalOpen, setPackDataModalOpen] = useState(false);
  const [packDataLengthCm, setPackDataLengthCm] = useState<number | null>(null);
  const [packDataWidthCm, setPackDataWidthCm] = useState<number | null>(null);
  const [packDataHeightCm, setPackDataHeightCm] = useState<number | null>(null);
  const [packDataWeightG, setPackDataWeightG] = useState<number | null>(null);
  const [savedPackData, setSavedPackData] = useState<{ lengthCm: number; widthCm: number; heightCm: number; weightG: number } | null>(null);
  const [buyWaybillModalOpen, setBuyWaybillModalOpen] = useState(false);
  const [buyWaybillSubmitting, setBuyWaybillSubmitting] = useState(false);
  const [buyWaybillCarrier, setBuyWaybillCarrier] = useState('');
  const [buyWaybillTrackingNo, setBuyWaybillTrackingNo] = useState('');
  const [buyWaybillFileList, setBuyWaybillFileList] = useState<any[]>([]);
  const [buyWaybillChargeAmount, setBuyWaybillChargeAmount] = useState<number | null>(null);
  const [buyWaybillChargeCurrency, setBuyWaybillChargeCurrency] = useState('USD');
  const [buyWaybillChargeNote, setBuyWaybillChargeNote] = useState('');
  const [buyWaybillProviderOptions, setBuyWaybillProviderOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [localWaybillCarrier, setLocalWaybillCarrier] = useState('');
  const [localWaybillTrackingNo, setLocalWaybillTrackingNo] = useState('');
  const [localWaybillChargeAmount, setLocalWaybillChargeAmount] = useState('');
  const [localWaybillChargeCurrency, setLocalWaybillChargeCurrency] = useState('');
  const [localWaybillChargeNote, setLocalWaybillChargeNote] = useState('');
  const [localAttachmentsDetail, setLocalAttachmentsDetail] = useState<any[]>([]);
  const [localOperationRecords, setLocalOperationRecords] = useState<any[]>([]);
  const audioCtxRef = useRef<any>(null);
  const audioUnlockedRef = useRef<boolean>(false);
  const [pickVerifyOpen, setPickVerifyOpen] = useState(false);
  const [pickScanInput, setPickScanInput] = useState('');
  const [pickVerifyMap, setPickVerifyMap] = useState<Record<string, number>>({});
  const [pickVerifyTempMap, setPickVerifyTempMap] = useState<Record<string, number>>({});
  const pickScanInputRef = useRef<any>(null);
  const [createIocSubmitting, setCreateIocSubmitting] = useState(false);
  const [createdIocCode, setCreatedIocCode] = useState('');
  const [opRecordPage, setOpRecordPage] = useState(1);
  const [opRecordPageSize, setOpRecordPageSize] = useState(10);
  const [processResultStatus, setProcessResultStatus] = useState<'pending' | 'completed' | 'unable'>('pending');
  const [processUnableReason, setProcessUnableReason] = useState('');
  const [unableReasonModalOpen, setUnableReasonModalOpen] = useState(false);
  const [unableReasonQuickValue, setUnableReasonQuickValue] = useState('');
  const [unableReasonInput, setUnableReasonInput] = useState('');
  const [printOtherAttachmentsModalOpen, setPrintOtherAttachmentsModalOpen] = useState(false);
  const closeAllAfterPrintRef = useRef(false);
  const closePreviewAfterPrintRef = useRef(false);
  const draftSaveTimerRef = useRef<any>(null);
  const draftRestoredRef = useRef<boolean>(false);
  const restoredLocalPackDataRef = useRef<boolean>(false);
  const restoredLocalProcessResultRef = useRef<boolean>(false);
  const restoredLocalWaybillRef = useRef<boolean>(false);

  useEffect(() => {
    try {
      const src = (metrics as any)?.pick_verify;
      if (src && typeof src === 'object') {
        const next: Record<string, number> = {};
        Object.keys(src || {}).forEach((k) => {
          const v: any = (src as any)[k];
          if (v === true) {
            next[normalizeCode(k)] = 1;
            return;
          }
          const n = Number(v || 0);
          if (Number.isFinite(n) && n > 0) next[normalizeCode(k)] = n;
        });
        setPickVerifyMap(next);
      }
    } catch {}
  }, [metrics]);
  useEffect(() => {
    const backendCarrier = sanitizeNullableText(instructionDetail?.waybill_carrier);
    const backendTrackingNo = sanitizeNullableText(instructionDetail?.waybill_tracking_no);
    const chargeInfo = (instructionDetail as any)?.buy_waybill_info?.charge_info || {};
    const backendChargeAmount = sanitizeNullableText(chargeInfo?.amount);
    const backendChargeCurrency = sanitizeNullableText(chargeInfo?.currency);
    const backendChargeNote = sanitizeNullableText(chargeInfo?.note);
    // 未恢复本地草稿：全量以后端为准
    if (!restoredLocalWaybillRef.current) {
      setLocalWaybillCarrier(backendCarrier);
      setLocalWaybillTrackingNo(backendTrackingNo);
      setLocalWaybillChargeAmount(backendChargeAmount);
      setLocalWaybillChargeCurrency(backendChargeCurrency);
      setLocalWaybillChargeNote(backendChargeNote);
    } else {
      // 已恢复本地草稿：仅当后端对应字段非空时才覆盖该字段，避免空值抹掉本地草稿
      if (backendCarrier) setLocalWaybillCarrier(backendCarrier);
      if (backendTrackingNo) setLocalWaybillTrackingNo(backendTrackingNo);
      if (backendChargeAmount) setLocalWaybillChargeAmount(backendChargeAmount);
      if (backendChargeCurrency) setLocalWaybillChargeCurrency(backendChargeCurrency);
      if (backendChargeNote) setLocalWaybillChargeNote(backendChargeNote);
    }
    setLocalAttachmentsDetail(Array.isArray(instructionDetail?.attachments_detail) ? instructionDetail.attachments_detail : []);
  }, [instructionDetail?.waybill_carrier, instructionDetail?.waybill_tracking_no, instructionDetail?.attachments_detail, instructionDetail?.buy_waybill_info]);
  useEffect(() => {
    const arr = Array.isArray(metrics?.operation_records) ? metrics.operation_records : [];
    setLocalOperationRecords(arr.slice(0, 100));
  }, [metrics?.operation_records]);
  useEffect(() => {
    const code = sanitizeNullableText((metrics as any)?.created_ioc_code || (data as any)?.created_ioc_code);
    if (code) setCreatedIocCode(code);
  }, [metrics?.created_ioc_code, data?.created_ioc_code]);
  useEffect(() => {
    const rawStatus = String((metrics as any)?.process_result_status || '').trim().toLowerCase();
    const rawReason = sanitizeNullableText((metrics as any)?.process_unable_reason);
    const hasStatus = rawStatus === 'completed' || rawStatus === 'unable' || rawStatus === 'pending';
    const hasReason = !!rawReason;
    // 已恢复本地草稿时，后端若未提供明确值则不覆盖
    if (restoredLocalProcessResultRef.current && !hasStatus && !hasReason) return;
    if (rawStatus === 'completed') setProcessResultStatus('completed');
    else if (rawStatus === 'unable') setProcessResultStatus('unable');
    else if (rawStatus === 'pending') setProcessResultStatus('pending');
    setProcessUnableReason(rawReason);
    if (hasStatus || hasReason) restoredLocalProcessResultRef.current = false;
  }, [metrics]);
  const draftStorageKey = useMemo(() => {
    const instructionId = Number((instructionDetail as any)?.id || 0);
    if (!Number.isFinite(instructionId) || instructionId <= 0) return '';
    const t = Number(taskId || 0);
    const taskPart = Number.isFinite(t) && t > 0 ? String(t) : 'NO_TASK';
    const sessionRaw = String(executionSessionId || '').trim();
    const sessionPart = sessionRaw || 'NO_SESSION';
    return `SINGLE_DROPSHIP_PACK_DRAFT_${instructionId}_${taskPart}_${sessionPart}`;
  }, [executionSessionId, instructionDetail?.id, taskId]);
  useEffect(() => {
    if (!draftStorageKey) return;
    const instructionId = Number((instructionDetail as any)?.id || 0);
    const t = Number(taskId || 0);
    if (!Number.isFinite(instructionId) || instructionId <= 0) return;
    const taskPart = Number.isFinite(t) && t > 0 ? String(t) : 'NO_TASK';
    const prefix = `SINGLE_DROPSHIP_PACK_DRAFT_${instructionId}_${taskPart}_`;
    const legacyKey = `SINGLE_DROPSHIP_PACK_DRAFT_${instructionId}_${taskPart}`;
    try {
      const keysToDelete: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = String(localStorage.key(i) || '');
        if (!k) continue;
        if (k === legacyKey) {
          keysToDelete.push(k);
          continue;
        }
        if (k.startsWith(prefix) && k !== draftStorageKey) {
          keysToDelete.push(k);
        }
      }
      keysToDelete.forEach((k) => {
        try { localStorage.removeItem(k); } catch {}
      });
    } catch {}
  }, [draftStorageKey, instructionDetail, taskId]);
  useEffect(() => {
    draftRestoredRef.current = false;
    restoredLocalPackDataRef.current = false;
    restoredLocalProcessResultRef.current = false;
    restoredLocalWaybillRef.current = false;
    // 切换到新任务/指令时先清空，避免沿用上一个页面的包装数据
    setSavedPackData(null);
    setProcessResultStatus('pending');
    setProcessUnableReason('');
    setLocalWaybillCarrier('');
    setLocalWaybillTrackingNo('');
    setLocalWaybillChargeAmount('');
    setLocalWaybillChargeCurrency('');
    setLocalWaybillChargeNote('');
  }, [draftStorageKey]);
  useEffect(() => {
    if (!draftStorageKey || draftRestoredRef.current) return;
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) {
        draftRestoredRef.current = true;
        return;
      }
      const d = JSON.parse(raw) || {};
      const saved = d?.savedPackData;
      let hasLocalSavedPackData = false;
      if (
        saved
        && Number.isFinite(Number(saved?.lengthCm))
        && Number.isFinite(Number(saved?.widthCm))
        && Number.isFinite(Number(saved?.heightCm))
        && Number.isFinite(Number(saved?.weightG))
      ) {
        setSavedPackData({
          lengthCm: Math.round(Number(saved.lengthCm)),
          widthCm: Math.round(Number(saved.widthCm)),
          heightCm: Math.round(Number(saved.heightCm)),
          weightG: Math.round(Number(saved.weightG)),
        });
        hasLocalSavedPackData = true;
      }
      restoredLocalPackDataRef.current = hasLocalSavedPackData;
      const pvm = d?.pickVerifyMap;
      if (pvm && typeof pvm === 'object') {
        const next: Record<string, number> = {};
        Object.keys(pvm).forEach((k) => {
          const n = Number((pvm as any)[k] || 0);
          if (Number.isFinite(n) && n > 0) next[normalizeCode(k)] = n;
        });
        setPickVerifyMap(next);
      }
      const prs = String(d?.processResultStatus || '').trim();
      if (prs === 'completed' || prs === 'unable' || prs === 'pending') {
        setProcessResultStatus(prs as any);
        restoredLocalProcessResultRef.current = true;
      }
      const pur = sanitizeNullableText(d?.processUnableReason);
      setProcessUnableReason(pur);
      if (pur) restoredLocalProcessResultRef.current = true;
      const ops = Array.isArray(d?.localOperationRecords) ? d.localOperationRecords : [];
      if (ops.length > 0) setLocalOperationRecords(ops.slice(0, 100));
      const ioc = sanitizeNullableText(d?.createdIocCode);
      if (ioc) setCreatedIocCode(ioc);
      const wCarrier = sanitizeNullableText(d?.localWaybillCarrier);
      const wTracking = sanitizeNullableText(d?.localWaybillTrackingNo);
      const wChargeAmount = sanitizeNullableText(d?.localWaybillChargeAmount);
      const wChargeCurrency = sanitizeNullableText(d?.localWaybillChargeCurrency);
      const wChargeNote = sanitizeNullableText(d?.localWaybillChargeNote);
      const hasLocalWaybillDraft = !!(wCarrier || wTracking || wChargeAmount || wChargeCurrency || wChargeNote);
      if (hasLocalWaybillDraft) {
        setLocalWaybillCarrier(wCarrier);
        setLocalWaybillTrackingNo(wTracking);
        setLocalWaybillChargeAmount(wChargeAmount);
        setLocalWaybillChargeCurrency(wChargeCurrency);
        setLocalWaybillChargeNote(wChargeNote);
        restoredLocalWaybillRef.current = true;
      }
    } catch {}
    draftRestoredRef.current = true;
  }, [draftStorageKey]);
  useEffect(() => {
    if (!draftStorageKey || !draftRestoredRef.current) return;
    try { if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current); } catch {}
    draftSaveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(draftStorageKey, JSON.stringify({
          version: 1,
          updated_at: Date.now(),
          savedPackData: savedPackData || null,
          pickVerifyMap: pickVerifyMap || {},
          processResultStatus,
          processUnableReason: String(processUnableReason || ''),
          localOperationRecords: (Array.isArray(localOperationRecords) ? localOperationRecords : []).slice(0, 100),
          createdIocCode: String(createdIocCode || ''),
          localWaybillCarrier: String(localWaybillCarrier || ''),
          localWaybillTrackingNo: String(localWaybillTrackingNo || ''),
          localWaybillChargeAmount: String(localWaybillChargeAmount || ''),
          localWaybillChargeCurrency: String(localWaybillChargeCurrency || ''),
          localWaybillChargeNote: String(localWaybillChargeNote || ''),
        }));
      } catch {}
    }, 350);
    return () => {
      try { if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current); } catch {}
    };
  }, [
    draftStorageKey,
    savedPackData,
    pickVerifyMap,
    processResultStatus,
    processUnableReason,
    localOperationRecords,
    createdIocCode,
    localWaybillCarrier,
    localWaybillTrackingNo,
    localWaybillChargeAmount,
    localWaybillChargeCurrency,
    localWaybillChargeNote,
  ]);
  useEffect(() => {
    if (!buyWaybillModalOpen) return;
    if (buyWaybillProviderOptions.length > 0) return;
    (async () => {
      try {
        const resp = await request.get('/api/v1/logistics/providers/', { params: { is_active: 1, page_size: 1000 } });
        const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
        const options = list
          .map((item: any) => {
            const code = String(item?.code || '').trim();
            const name = String(item?.name || '').trim();
            if (!code) return null;
            return { value: code, label: name ? `${code} (${name})` : code };
          })
          .filter(Boolean) as Array<{ label: string; value: string }>;
        setBuyWaybillProviderOptions(options);
      } catch {
        setBuyWaybillProviderOptions([]);
      }
    })();
  }, [buyWaybillModalOpen, buyWaybillProviderOptions.length]);

  const inventoryRows = useMemo(() => {
    try {
      const td = Array.isArray(instructionDetail?.targets_detail) ? instructionDetail.targets_detail : [];
      if (td.length > 0) {
        return td.slice(0, 200).map((t:any, idx:number) => ({
          key: `${idx}-${String(t?.inventory_unit_system_barcode || t?.id || '')}`,
          project: (() => {
            const snap = t?.target?.snapshot || t?.snapshot || {};
            const code = t?.project_code || snap?.project_code || snap?.code;
            const name = t?.project_name || snap?.project_name || snap?.name;
            const txt = (code || name) ? `${code || ''}${name ? (code ? ' - ' : '') + name : ''}` : '-';
            return normalizeText(txt);
          })(),
          target_label: normalizeText(t?.snapshot?.system_barcode || t?.inventory_unit_system_barcode || t?.target?.inventory_unit_system_barcode || t?.fingerprint || '-'),
          sorting_barcode: (() => {
            const items = extractUnitContentItems(t);
            const uid = String(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? '');
            const cached = uid ? (unitContentCache[uid] || []) : [];
            const all = items.length > 0 ? items : cached;
            if (all.length > 0) return all.map((it) => normalizeText(it.barcode || '-')).filter((x) => x && x !== '-').join('、') || '-';
            const fb = normalizeText(
              t?.product_sorting_code
              || t?.snapshot?.product_sorting_code
              || t?.sorting_code
              || '-',
            );
            return fb;
          })(),
          target_qty: Number(t?.expected_qty || 0),
          inventory_unit_id: t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? null,
        }));
      }
      const src = Array.isArray(data?.pick_rows) ? data.pick_rows : [];
      return src.slice(0, 50).map((r:any, idx:number) => ({
        key: `${idx}-${String(r?.target_label || '')}`,
        project: normalizeText(r?.project || r?.project_code || r?.project_name || '-'),
        target_label: normalizeText(r?.target_label || '-'),
        sorting_barcode: (() => {
          const items = extractUnitContentItems(r);
          if (items.length > 0) return items.map((it) => normalizeText(it.barcode || '-')).filter((x) => x && x !== '-').join('、') || '-';
          return normalizeText(r?.product_sorting_code || r?.sorting_code || '-');
        })(),
        target_qty: Number(r?.target_qty || 0),
        inventory_unit_id: r?.target?.inventory_unit_id ?? r?.inventory_unit_id ?? null,
      }));
    } catch {
      return [];
    }
  }, [instructionDetail?.targets_detail, data?.pick_rows, unitContentCache]);

  const waybillStatusText = useMemo(() => {
    const source = String(
      instructionDetail?.waybill_source
      || instructionDetail?.customer_answers?.waybill_source
      || '',
    ).trim().toLowerCase();
    const mode = String(
      instructionDetail?.waybill_submit_mode
      || instructionDetail?.customer_answers?.waybill_submit_mode
      || '',
    ).trim().toLowerCase();
    if (source === 'warehouse') return '仓库提供运单';
    if (source === 'upload' && mode === 'after_pack') return '先申请包装数据';
    if (source === 'upload' || mode === 'immediate') return '有运单';
    return '-';
  }, [instructionDetail?.waybill_source, instructionDetail?.waybill_submit_mode, instructionDetail?.customer_answers]);

  const packMethodText = useMemo(() => {
    const methodRaw = mapPackingMethodToZh(instructionDetail?.estimated_packing_method);
    const method = methodRaw === '-' ? '' : methodRaw;
    const pkg = instructionDetail?.estimated_packaging_detail || instructionDetail?.estimated_packaging || null;
    const pkgNameRaw = normalizeText(pkg?.name || pkg?.display_name || pkg?.system_code || pkg?.code || '');
    const pkgName = pkgNameRaw === '-' ? '' : pkgNameRaw;
    if (method && pkgName) return `${method} | ${pkgName}`;
    if (method) return method;
    if (pkgName) return pkgName;
    return '-';
  }, [instructionDetail?.estimated_packing_method, instructionDetail?.estimated_packaging_detail, instructionDetail?.estimated_packaging]);

  const [recommendedPackCode, setRecommendedPackCode] = useState('');
  const [recommendedPackName, setRecommendedPackName] = useState('');
  const [recommendLoading, setRecommendLoading] = useState(false);
  const recommendedPackDisplay = useMemo(() => {
    const code = sanitizeNullableText(recommendedPackCode);
    const name = sanitizeNullableText(recommendedPackName);
    if (!code && !name) return { code: '', name: '' };
    const existingPkgName = sanitizeNullableText(
      instructionDetail?.estimated_packaging_detail?.name
      || instructionDetail?.estimated_packaging_detail?.display_name
      || instructionDetail?.estimated_packaging_detail?.system_code
      || instructionDetail?.estimated_packaging_detail?.code
      || instructionDetail?.estimated_packaging?.name
      || instructionDetail?.estimated_packaging?.display_name
      || instructionDetail?.estimated_packaging?.system_code
      || instructionDetail?.estimated_packaging?.code,
    );
    const dedupedName = (name && existingPkgName && name.toLowerCase() === existingPkgName.toLowerCase()) ? '' : name;
    return { code, name: dedupedName };
  }, [
    recommendedPackCode,
    recommendedPackName,
    instructionDetail?.estimated_packaging_detail,
    instructionDetail?.estimated_packaging,
  ]);
  const isDropshipInstruction = useMemo(() => {
    const tName = String(instructionDetail?.template_snapshot?.name || instructionDetail?.template_detail?.name || '').trim();
    const tCode = String(instructionDetail?.template_snapshot?.code || instructionDetail?.template_detail?.code || '').trim().toLowerCase();
    return tName.includes('一件代发') || tCode.includes('dropship');
  }, [instructionDetail?.template_snapshot, instructionDetail?.template_detail]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!isDropshipInstruction || instructionDetail?.is_pack_required !== true) {
          if (active) { setRecommendedPackCode(''); setRecommendedPackName(''); setRecommendLoading(false); }
          return;
        }
        const category = String(instructionDetail?.estimated_packing_method || '').trim();
        if (!['warehouse_free', 'warehouse_paid', 'customer_carton'].includes(category)) {
          if (active) { setRecommendedPackCode(''); setRecommendedPackName(''); setRecommendLoading(false); }
          return;
        }
        if (active) setRecommendLoading(true);
        const targets = Array.isArray(instructionDetail?.targets_detail) ? instructionDetail.targets_detail : [];
        const iuTargets = targets.filter((t: any) => String(t?.target_type || '').trim() === 'inventory_unit');
        const perOrderQty = Math.max(1, iuTargets.reduce((sum: number, t: any) => sum + (Number(t?.expected_qty ?? 0) || 0), 0));
        const firstTarget = iuTargets[0] || targets[0];
        const warehouseId = instructionDetail?.warehouse_id || instructionDetail?.instruction_warehouse_id || undefined;
        const customerId = instructionDetail?.customer_id || instructionDetail?.instruction_customer_id || undefined;
        const iuId = firstTarget?.inventory_unit_id || firstTarget?.target?.inventory_unit_id;
        let items: any[] = [];
        if (iuId && Number.isFinite(Number(iuId))) {
          try {
            const iuResp = await getInventoryUnitDetail(Number(iuId), warehouseId ? { warehouse_id: warehouseId } : undefined);
            const iu = (iuResp as any)?.data ?? iuResp;
            const l = Number(iu?.actual_length ?? iu?.length ?? 0);
            const w = Number(iu?.actual_width ?? iu?.width ?? 0);
            const h = Number(iu?.actual_height ?? iu?.height ?? 0);
            if (Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) && l > 0 && w > 0 && h > 0) {
              items = [{ length_cm: l, width_cm: w, height_cm: h, weight_g: 0, quantity: perOrderQty }];
            }
          } catch {}
        }
        if (!items.length) {
          const snap = firstTarget?.snapshot || {};
          const l = Number(firstTarget?.actual_length ?? snap?.actual_length ?? 0);
          const w = Number(firstTarget?.actual_width ?? snap?.actual_width ?? 0);
          const h = Number(firstTarget?.actual_height ?? snap?.actual_height ?? 0);
          if (Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) && l > 0 && w > 0 && h > 0) {
            items = [{ length_cm: l, width_cm: w, height_cm: h, weight_g: 0, quantity: perOrderQty }];
          }
        }
        if (!items.length) {
          if (active) { setRecommendedPackCode(''); setRecommendedPackName(''); }
          return;
        }
        const resp = await request.post('/api/v1/logistics/packaging-recommendation/', {
          items,
          customer_id: customerId,
          warehouse_id: warehouseId,
          category,
        });
        const box = (resp?.data as any)?.recommended_box;
        if (!active) return;
        if (!box) {
          setRecommendedPackCode('');
          setRecommendedPackName('');
          return;
        }
        const code = normalizeText(box?.system_code || '');
        const name = normalizeText(box?.name || box?.title || '');
        setRecommendedPackCode(code === '-' ? '' : code);
        setRecommendedPackName(name === '-' ? '' : name);
      } catch {
        if (active) { setRecommendedPackCode(''); setRecommendedPackName(''); }
      } finally {
        if (active) setRecommendLoading(false);
      }
    })();
    return () => { active = false; };
  }, [instructionDetail, isDropshipInstruction]);

  const waybillAttachments = useMemo(() => {
    const arr = Array.isArray(localAttachmentsDetail) ? localAttachmentsDetail : [];
    return arr.filter((a:any) => String(a?.label || '').trim() === '物流运单');
  }, [localAttachmentsDetail]);

  const waybillAttachmentText = useMemo(() => {
    if (!waybillAttachments.length) return '-';
    return waybillAttachments.map((a:any, idx:number) => String(a?.file_name || a?.name || a?.file || a?.file_path || `运单附件${idx + 1}`)).join('；');
  }, [waybillAttachments]);

  useEffect(() => {
    const l = Number(instructionDetail?.actual_pack_length_cm ?? 0);
    const w = Number(instructionDetail?.actual_pack_width_cm ?? 0);
    const h = Number(instructionDetail?.actual_pack_height_cm ?? 0);
    const g = Number(instructionDetail?.actual_gross_weight_g ?? 0);
    if ([l, w, h, g].every((n) => Number.isFinite(n) && n > 0)) {
      setSavedPackData({ lengthCm: l, widthCm: w, heightCm: h, weightG: g });
      restoredLocalPackDataRef.current = false;
      return;
    }
    // 后端无包装数据时不覆盖本地恢复/本地编辑数据
  }, [
    instructionDetail?.actual_pack_length_cm,
    instructionDetail?.actual_pack_width_cm,
    instructionDetail?.actual_pack_height_cm,
    instructionDetail?.actual_gross_weight_g,
  ]);

  const packagingDataNode = useMemo<React.ReactNode>(() => {
    if (!savedPackData) return '-';
    const toIn = (cm: number) => (cm / 2.54).toFixed(2);
    const totalOz = savedPackData.weightG / 28.349523125;
    const lb = Math.floor(totalOz / 16);
    const oz = totalOz - lb * 16;
    const metricText = `尺寸 ${savedPackData.lengthCm} × ${savedPackData.widthCm} × ${savedPackData.heightCm} cm  重量 ${savedPackData.weightG} g`;
    const imperialText = `尺寸 ${toIn(savedPackData.lengthCm)} × ${toIn(savedPackData.widthCm)} × ${toIn(savedPackData.heightCm)} in  重量 ${lb} lb ${oz.toFixed(2)} oz`;
    return (
      <span>
        <span>{metricText}</span>
        <span style={{ margin: '0 20px' }}>|</span>
        <span>{imperialText}</span>
      </span>
    );
  }, [savedPackData]);

  const openPackDataModal = async () => {
    const src = savedPackData;
    if (src) {
      setPackDataLengthCm(Number(src.lengthCm));
      setPackDataWidthCm(Number(src.widthCm));
      setPackDataHeightCm(Number(src.heightCm));
      setPackDataWeightG(Number(src.weightG));
      setPackDataModalOpen(true);
      return;
    }
    // 无需打包时，默认带入该库存单元实际尺寸/重量，便于快速确认与提交。
    if (instructionDetail?.is_pack_required === false) {
      const targets = Array.isArray(instructionDetail?.targets_detail) ? instructionDetail.targets_detail : [];
      const firstTarget = targets.find((t: any) => String(t?.target_type || '').trim() === 'inventory_unit') || targets[0];
      const snap = firstTarget?.snapshot || {};
      const parsePositiveNum = (v: any): number | null => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      const trySetByRaw = (raw: any) => {
        if (!raw) return false;
        const l = parsePositiveNum(raw?.actual_length ?? raw?.length_cm ?? raw?.length);
        const w = parsePositiveNum(raw?.actual_width ?? raw?.width_cm ?? raw?.width);
        const h = parsePositiveNum(raw?.actual_height ?? raw?.height_cm ?? raw?.height);
        const g = parsePositiveNum(
          raw?.actual_weight_g
          ?? raw?.actual_gross_weight_g
          ?? raw?.gross_weight_g
          ?? raw?.weight_g
          ?? raw?.actual_weight
          ?? raw?.weight,
        );
        if (l && w && h && g) {
          setPackDataLengthCm(Math.round(l));
          setPackDataWidthCm(Math.round(w));
          setPackDataHeightCm(Math.round(h));
          setPackDataWeightG(Math.round(g));
          return true;
        }
        return false;
      };
      if (!trySetByRaw(firstTarget) && !trySetByRaw(snap)) {
        try {
          const iuId = firstTarget?.inventory_unit_id || firstTarget?.target?.inventory_unit_id;
          const warehouseId = instructionDetail?.warehouse_id || instructionDetail?.instruction_warehouse_id || undefined;
          if (iuId && Number.isFinite(Number(iuId))) {
            const iuResp = await getInventoryUnitDetail(Number(iuId), warehouseId ? { warehouse_id: warehouseId } : undefined);
            const iu = (iuResp as any)?.data ?? iuResp;
            trySetByRaw(iu);
          }
        } catch {}
      }
    } else {
      setPackDataLengthCm(null);
      setPackDataWidthCm(null);
      setPackDataHeightCm(null);
      setPackDataWeightG(null);
    }
    setPackDataModalOpen(true);
  };
  const openBuyWaybillModal = () => {
    setBuyWaybillCarrier(sanitizeNullableText(localWaybillCarrier));
    setBuyWaybillTrackingNo(sanitizeNullableText(localWaybillTrackingNo));
    const chargeInfo = (instructionDetail as any)?.buy_waybill_info?.charge_info || {};
    const amountRaw = chargeInfo?.amount;
    setBuyWaybillChargeAmount(amountRaw == null || amountRaw === '' ? null : Number(amountRaw));
    setBuyWaybillChargeCurrency(String(chargeInfo?.currency || 'USD').trim().toUpperCase() || 'USD');
    setBuyWaybillChargeNote(String(chargeInfo?.note || '').trim());
    setBuyWaybillFileList([]);
    setBuyWaybillModalOpen(true);
  };
  const submitBuyWaybillUpload = async () => {
    const instructionId = Number(instructionDetail?.id || 0);
    if (!instructionId) return void message.warning('缺少指令ID');
    const carrier = String(buyWaybillCarrier || '').trim();
    const trackingNo = String(buyWaybillTrackingNo || '').trim();
    const chargeAmountInput = buyWaybillChargeAmount;
    const chargeAmount = Number(chargeAmountInput);
    const chargeCurrency = String(buyWaybillChargeCurrency || 'USD').trim().toUpperCase();
    const chargeNote = String(buyWaybillChargeNote || '').trim();
    const fileObj = buyWaybillFileList?.[0]?.originFileObj;
    const hasExistingWaybill = waybillAttachments.length > 0;
    if (!carrier) return void message.warning('请填写运单所属承运商');
    if (!trackingNo) return void message.warning('请填写物流跟踪号');
    if (!fileObj && !hasExistingWaybill) return void message.warning('请上传运单附件');
    if (chargeAmountInput == null || !Number.isFinite(chargeAmount)) return void message.warning('请填写运单费用金额');
    if (chargeAmount < 0) return void message.warning('运单费用金额不能小于0');
    if (chargeAmount === 0) {
      const proceedZero = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '运单费用金额为0，确认提交吗？',
          content: '该运单将视为仓库免费提供，不会产生“出库尾程物流”费用。',
          okText: '确认提交',
          cancelText: '返回修改',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!proceedZero) return;
    }
    if (!chargeCurrency) return void message.warning('请填写运单费用币种');
    let ignoreDuplicateWarning = false;
    try {
      const precheckResp = await request.post('/api/v1/instruction/instructions/precheck/', {
        waybill_carrier: carrier,
        waybill_tracking_no: trackingNo,
        exclude_instruction_ids: [instructionId],
      });
      const warnings = Array.isArray((precheckResp as any)?.data?.warnings)
        ? (precheckResp as any).data.warnings
        : (Array.isArray((precheckResp as any)?.warnings) ? (precheckResp as any).warnings : []);
      if (warnings.length > 0) {
        const proceed = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: `检测到${warnings.length}条物流跟踪号重复记录，是否继续提交？`,
            content: warnings.map((w: any, i: number) => `${i + 1}. ${w.provider || '-'} / ${w.tracking_number || '-'}`).join('\n'),
            okText: '继续提交',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!proceed) return;
        ignoreDuplicateWarning = true;
      }
    } catch {
      return void message.error('物流跟踪号唯一性校验失败，请稍后重试');
    }
    const formData = new FormData();
    formData.append('waybill_carrier', carrier);
    formData.append('waybill_tracking_no', trackingNo);
    formData.append('waybill_charge_amount', String(chargeAmount));
    formData.append('waybill_charge_currency', chargeCurrency);
    formData.append('waybill_charge_note', chargeNote);
    formData.append('ignore_duplicate_warning', ignoreDuplicateWarning ? '1' : '0');
    if (fileObj) formData.append('file', fileObj);
    setBuyWaybillSubmitting(true);
    try {
      const resp = await request.post(`/api/v1/instruction/instructions/${instructionId}/waybill-upload/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = resp?.data || {};
      setLocalWaybillCarrier(String(payload?.waybill_carrier ?? carrier).trim());
      setLocalWaybillTrackingNo(String(payload?.waybill_tracking_no ?? trackingNo).trim());
      setLocalWaybillChargeAmount(sanitizeNullableText(payload?.waybill_charge_amount ?? String(chargeAmount)));
      setLocalWaybillChargeCurrency(sanitizeNullableText(payload?.waybill_charge_currency ?? chargeCurrency));
      setLocalWaybillChargeNote(sanitizeNullableText(payload?.waybill_charge_note ?? chargeNote));
      const newAtts = Array.isArray(payload?.attachments_detail) ? payload.attachments_detail : localAttachmentsDetail;
      setLocalAttachmentsDetail(Array.isArray(newAtts) ? newAtts : []);
      try {
        onChange?.('waybill_carrier', String(payload?.waybill_carrier ?? carrier).trim());
        onChange?.('waybill_tracking_no', String(payload?.waybill_tracking_no ?? trackingNo).trim());
        onChange?.('waybill_charge_amount', sanitizeNullableText(payload?.waybill_charge_amount ?? String(chargeAmount)));
        onChange?.('waybill_charge_currency', sanitizeNullableText(payload?.waybill_charge_currency ?? chargeCurrency));
        onChange?.('waybill_charge_note', sanitizeNullableText(payload?.waybill_charge_note ?? chargeNote));
      } catch {}
      appendOperationRecord(
        '购买并上传运单',
        `${String(payload?.waybill_carrier ?? carrier).trim() || '-'} / ${String(payload?.waybill_tracking_no ?? trackingNo).trim() || '-'}`,
      );
      setBuyWaybillModalOpen(false);
      message.success('运单信息已更新');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '提交失败');
    } finally {
      setBuyWaybillSubmitting(false);
    }
  };
  const submitPackDataModal = () => {
    const l = Number(packDataLengthCm || 0);
    const w = Number(packDataWidthCm || 0);
    const h = Number(packDataHeightCm || 0);
    const g = Number(packDataWeightG || 0);
    if (![l, w, h, g].every((n) => Number.isFinite(n) && n > 0)) {
      message.warning('请填写大于0的整数（长宽高cm、重量g）');
      return;
    }
    setSavedPackData({ lengthCm: Math.round(l), widthCm: Math.round(w), heightCm: Math.round(h), weightG: Math.round(g) });
    try {
      onChange?.('actual_pack_length_cm', Math.round(l));
      onChange?.('actual_pack_width_cm', Math.round(w));
      onChange?.('actual_pack_height_cm', Math.round(h));
      onChange?.('actual_gross_weight_g', Math.round(g));
      onChange?.('pack_result_status', 'provided');
    } catch {}
    appendOperationRecord(
      '输入包装数据',
      `LWH ${Math.round(l)}×${Math.round(w)}×${Math.round(h)} cm, ${Math.round(g)} g`,
    );
    setPackDataModalOpen(false);
    message.success('包装数据已保存');
  };
  const resolveAttachmentUrl = (att: any): string => {
    try {
      const raw = String(
        att?.url
        || att?.file_url
        || att?.download_url
        || att?.downloadUrl
        || att?.file
        || att?.file_path
        || att?.path
        || att?.file_name
        || '',
      ).trim();
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      let backendOrigin = window.location.origin;
      try {
        const u = new URL(backendOrigin);
        if (u.port === '3000') {
          u.port = '8000';
          backendOrigin = u.toString().replace(/\/$/, '');
        }
      } catch {}
      let path = raw;
      if (!path.startsWith('/')) path = `/${path}`;
      if (/^\/(instruction|attachments?)/i.test(path)) path = `/media${path}`;
      else if (!/^\/media\//i.test(path)) path = `/media${path}`;
      return `${backendOrigin}${path}`;
    } catch {
      return '';
    }
  };

  const openAttachmentPreview = async (att: any) => {
    const url = resolveAttachmentUrl(att);
    if (!url) {
      message.warning('无可预览附件');
      return;
    }
    setPreviewLoading(true);
    const name = String(att?.file_name || att?.name || att?.file || '').toLowerCase();
    const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(name);
    const isPdf = /\.pdf$/i.test(name);
    setPreviewTitle(String(att?.file_name || att?.name || '运单附件预览'));
    setPreviewPrintText(getAttachmentPrintText(att));
    try {
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('预览文件加载失败');
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      setPreviewType((isImage || isPdf) ? (isImage ? 'image' : 'file') : 'file');
      setPreviewUrl(objectUrl);
      setPreviewOpen(true);
    } catch {
      setPreviewType('file');
      setPreviewUrl(url);
      setPreviewOpen(true);
    } finally {
      setPreviewLoading(false);
    }
  };
  const deleteWaybillAttachment = async (att: any) => {
    const attId = Number(att?.id || 0);
    if (!attId) return void message.warning('附件ID无效，无法删除');
    const fileName = String(att?.file_name || att?.name || att?.file || att?.file_path || `附件#${attId}`);
    const ok = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '删除运单附件',
        content: `确认删除运单附件「${fileName}」吗？`,
        okText: '删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    try {
      await request.delete(`/api/v1/instruction/attachments/${attId}/`);
      setLocalAttachmentsDetail((prev) => (Array.isArray(prev) ? prev.filter((x: any) => Number(x?.id || 0) !== attId) : []));
      message.success('运单附件已删除');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '删除失败');
    }
  };
  const previewBuyWaybillUploadFile = async (file: any) => {
    const fileName = String(file?.name || file?.originFileObj?.name || '运单附件预览');
    const lowerName = fileName.toLowerCase();
    const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(lowerName);
    setPreviewTitle(fileName);
    setPreviewPrintText('');
    try {
      const origin = file?.originFileObj;
      if (origin instanceof Blob) {
        const objectUrl = URL.createObjectURL(origin);
        setPreviewType(isImage ? 'image' : 'file');
        setPreviewUrl(objectUrl);
        setPreviewOpen(true);
        return;
      }
      const remoteUrl = String(file?.url || '').trim();
      if (remoteUrl) {
        setPreviewType(isImage ? 'image' : 'file');
        setPreviewUrl(remoteUrl);
        setPreviewOpen(true);
        return;
      }
      message.warning('无可预览文件');
    } catch {
      message.warning('预览失败');
    }
  };

  const closePreview = () => {
    try {
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    } catch {}
    closePreviewAfterPrintRef.current = false;
    setPreviewOpen(false);
    setPreviewUrl('');
    setPreviewPrintText('');
  };
  const closeAllTransientModals = () => {
    closeAllAfterPrintRef.current = false;
    closePreviewAfterPrintRef.current = false;
    setPreviewOpen(false);
    setPackDataModalOpen(false);
    setBuyWaybillModalOpen(false);
    setPickVerifyOpen(false);
    setPrintOtherAttachmentsModalOpen(false);
    setPreviewUrl('');
    setPreviewPrintText('');
  };
  useEffect(() => {
    const onAfterPrint = () => {
      if (closeAllAfterPrintRef.current) {
        closeAllAfterPrintRef.current = false;
        closeAllTransientModals();
        return;
      }
      if (closePreviewAfterPrintRef.current) {
        closePreviewAfterPrintRef.current = false;
        closePreview();
      }
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  const printPreview = () => {
    const finishPrintFlow = () => {
      if (closeAllAfterPrintRef.current) {
        closeAllAfterPrintRef.current = false;
        closeAllTransientModals();
        return;
      }
      if (closePreviewAfterPrintRef.current) {
        closePreviewAfterPrintRef.current = false;
        closePreview();
      }
    };
    try {
      if (previewType === 'file' && previewFrameRef.current?.contentWindow) {
        const frameWin = previewFrameRef.current.contentWindow;
        try {
          frameWin.addEventListener('afterprint', finishPrintFlow, { once: true } as any);
        } catch {}
        // 某些浏览器不会在当前窗口派发 afterprint，回到页面焦点时兜底收口。
        const onFocusFallback = () => {
          window.removeEventListener('focus', onFocusFallback);
          setTimeout(finishPrintFlow, 180);
        };
        window.addEventListener('focus', onFocusFallback, { once: true } as any);
        frameWin.print();
        return;
      }
      const w = window.open(previewUrl, '_blank');
      if (w) {
        try {
          w.addEventListener('afterprint', finishPrintFlow, { once: true } as any);
        } catch {}
        const onFocusFallback = () => {
          window.removeEventListener('focus', onFocusFallback);
          setTimeout(finishPrintFlow, 180);
        };
        window.addEventListener('focus', onFocusFallback, { once: true } as any);
        w.focus();
        setTimeout(() => { try { w.print(); } catch {} }, 350);
      } else {
        message.warning('浏览器拦截了打印窗口，请允许弹窗后重试');
      }
    } catch {
      message.warning('打印失败，请手动在预览窗口打印');
    }
  };
  const getWaybillAttachmentExt = (att: any): string => {
    const name = String(att?.file_name || att?.name || att?.file || att?.file_path || '').toLowerCase();
    const m = name.match(/\.([a-z0-9]+)(?:\?|#|$)/i);
    return (m?.[1] || '').toLowerCase();
  };
  const buildWaybillOverlayTextMinimal = (): string => {
    const fullCode = String(instructionDetail?.system_code || instructionCodeFromParent || '').trim();
    const codeParts = fullCode.split('-');
    const codeSuffix = (codeParts[codeParts.length - 1] || fullCode || '-').trim() || '-';
    const rows = Array.isArray(inventoryRows) ? inventoryRows : [];
    const totalQty = rows.reduce((sum: number, r: any) => sum + (Number(r?.target_qty || 0) || 0), 0);
    const typeChar = rows.length <= 1 ? 'S' : 'M';
    const typeStr = `${typeChar}${totalQty || 0}`;
    const parts = rows.slice(0, 2).map((r: any) => {
      const barcodeRaw = String(r?.sorting_barcode || '').trim();
      const firstCode = barcodeRaw.split(/[、,，\/|\s]+/g).map((x) => String(x || '').trim()).filter(Boolean)[0] || '';
      const tail = firstCode ? (firstCode.length >= 4 ? firstCode.slice(-4).toUpperCase() : firstCode.toUpperCase()) : '';
      const qty = Number(r?.target_qty || 0) || 0;
      return tail ? `${tail}×${qty}` : `×${qty}`;
    });
    const productStr = parts.join('/');
    const more = rows.length > 2 ? '...' : '';
    return `[${codeSuffix}][${typeStr}]${productStr ? `-${productStr}${more}` : ''}`;
  };
  const buildWaybillOverlayTextShortMinimal = (): string => {
    const fullCode = String(instructionDetail?.system_code || instructionCodeFromParent || '').trim();
    const codeParts = fullCode.split('-');
    const codeSuffix = (codeParts[codeParts.length - 1] || fullCode || '-').trim() || '-';
    const rows = Array.isArray(inventoryRows) ? inventoryRows : [];
    const totalQty = rows.reduce((sum: number, r: any) => sum + (Number(r?.target_qty || 0) || 0), 0);
    const typeChar = rows.length <= 1 ? 'S' : 'M';
    return `[${codeSuffix}][${typeChar}${totalQty || 0}]`;
  };
  const applyWaybillOverlayToBlobMinimal = async (blob: Blob, ext: string): Promise<Blob> => {
    const configObj: Record<string, any> = {
      layout_mode: 'sliding_window',
      content_source: 'custom_text',
      show_title: false,
      font_size: 42,
      font_bold: true,
      text_color: '#c41e3a',
      max_text_length: 50,
      max_label_area: 80000,
      blank_ratio_threshold: 0.98,
      blank_ratio_step: 2,
      bottom_band_ratio: 0.25,
      side_band_ratio: 0.2,
      slide_step: 30,
      margin: 24,
      custom_text: buildWaybillOverlayTextMinimal(),
      custom_text_fallback: buildWaybillOverlayTextShortMinimal(),
    };
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const s = (r.result as string) || '';
        resolve(s.includes(',') ? s.split(',')[1]! : s);
      };
      r.onerror = () => reject(new Error('文件读取失败'));
      r.readAsDataURL(blob);
    });
    const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
    const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
    const token = localStorage.getItem('token');
    const resp = await fetch(overlayUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_base64: base64,
        file_name: `attachment.${ext === 'pdf' ? 'pdf' : 'png'}`,
        config: configObj,
      }),
    });
    if (!resp.ok) {
      const errText = await resp.text();
      throw new Error(errText || resp.statusText);
    }
    return resp.blob();
  };
  const printWaybillDirectly = async () => {
    const selected = waybillAttachments[0];
    if (!selected) return void message.warning('暂无可打印的运单附件');
    const url = resolveAttachmentUrl(selected);
    if (!url) return void message.warning('无可预览附件');
    setPreviewLoading(true);
    try {
      const ext = getWaybillAttachmentExt(selected);
      const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
      const isPdf = ext === 'pdf';
      let finalBlob = await fetch(url, { credentials: 'include' as any }).then((r) => {
        if (!r.ok) throw new Error('附件加载失败');
        return r.blob();
      });
      if (isImg || isPdf) {
        try {
          finalBlob = await applyWaybillOverlayToBlobMinimal(finalBlob, isPdf ? 'pdf' : 'png');
          message.success(`已添加打印标识：${buildWaybillOverlayTextMinimal()}`);
        } catch {
          message.warning('添加打印标识失败，将直接打印原文件');
        }
      }
      const objectUrl = URL.createObjectURL(finalBlob);
      setPreviewTitle(String(selected?.file_name || selected?.name || '运单附件预览'));
      setPreviewPrintText(getAttachmentPrintText(selected));
      setPreviewType(isImg ? 'image' : 'file');
      setPreviewUrl(objectUrl);
      setPreviewOpen(true);
      appendOperationRecord('打印运单', String(selected?.file_name || selected?.name || '运单附件'));
    } catch (e: any) {
      setPreviewType('file');
      setPreviewUrl(url);
      setPreviewOpen(true);
      message.warning(e?.message || '附件预览失败，将尝试直接打印');
    } finally {
      setPreviewLoading(false);
    }
    setTimeout(() => {
      closeAllAfterPrintRef.current = true;
      printPreview();
      // 兜底：部分浏览器不触发 afterprint，避免界面卡在弹窗态
      setTimeout(() => {
        if (!closeAllAfterPrintRef.current) return;
        closeAllAfterPrintRef.current = false;
        closeAllTransientModals();
      }, 8000);
    }, 120);
  };
  const previewAndPrintIocLabel = async (code: string, totalQtyForDisplay?: number, kindsForDisplay?: number) => {
    const iocCode = String(code || '').trim();
    if (!iocCode) return;
    const instructionId = Number((instructionDetail as any)?.id || 0);
    if (!instructionId) return;
    setPreviewLoading(true);
    try {
      const toIn = (cm: number) => Number((cm / 2.54).toFixed(2));
      const toLb = (g: number) => Number((g / 453.59237).toFixed(3));
      const p = savedPackData || null;
      const rows = (inventoryRows || [])
        .map((r: any) => ({
          new_code: String(r?.target_label || '').trim() || '-',
          qty: Math.max(0, Math.floor(Number(r?.target_qty || 0) || 0)),
        }))
        .filter((x: any) => x.qty > 0);
      const totalQty = Number.isFinite(Number(totalQtyForDisplay)) ? Number(totalQtyForDisplay) : rows.reduce((sum: number, r: any) => sum + Number(r.qty || 0), 0);
      const kinds = Number.isFinite(Number(kindsForDisplay)) ? Number(kindsForDisplay) : rows.length;
      const payload: any = {
        instruction_id: instructionId,
        container_code: iocCode,
        instruction_code: String(instructionDetail?.system_code || instructionCodeFromParent || '-').trim() || '-',
        project_summary: String((inventoryRows?.[0] as any)?.project || '-').trim() || '-',
        container_type: 'box',
        container_type_display: '纸箱',
        created_at: new Date().toLocaleString(),
        dims_cm: {
          l: p?.lengthCm ?? '-',
          w: p?.widthCm ?? '-',
          h: p?.heightCm ?? '-',
        },
        dims_in: {
          l: p ? toIn(Number(p.lengthCm || 0)) : '-',
          w: p ? toIn(Number(p.widthCm || 0)) : '-',
          h: p ? toIn(Number(p.heightCm || 0)) : '-',
        },
        weight: {
          kg: p ? Number((Number(p.weightG || 0) / 1000).toFixed(3)) : '-',
          lb: p ? toLb(Number(p.weightG || 0)) : '-',
        },
        new_kinds: kinds,
        total_qty: totalQty,
        details: rows.slice(0, 20),
      };
      const resp = await request.post('/api/v1/storage/output-containers/labels/preview/', payload, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const objectUrl = URL.createObjectURL(blob);
      try {
        if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
      } catch {}
      setPreviewTitle(`IOC标签预览 - ${iocCode}`);
      setPreviewPrintText('');
      setPreviewType('file');
      setPreviewUrl(objectUrl);
      setPreviewOpen(true);
      appendOperationRecord('打印IOC标签', iocCode);
      setTimeout(() => {
        closePreviewAfterPrintRef.current = true;
        printPreview();
        // 兜底：部分浏览器不触发 afterprint
        setTimeout(() => {
          if (!closePreviewAfterPrintRef.current) return;
          closePreviewAfterPrintRef.current = false;
          closePreview();
        }, 8000);
      }, 120);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.response?.data?.detail || e?.message || '生成IOC标签失败');
    } finally {
      setPreviewLoading(false);
    }
  };
  const openPrintOtherAttachmentsModal = () => {
    if (!printableAttachmentsWithPrintRequirement.length) return void message.warning('暂无有打印需求的其他附件');
    setPrintOtherAttachmentsModalOpen(true);
  };
  const markProcessCompleted = () => {
    setProcessResultStatus('completed');
    setProcessUnableReason('');
    try {
      onChange?.('process_result_status', 'completed');
      onChange?.('process_unable_reason', '');
    } catch {}
    appendOperationRecord('标注已完成', '-');
    message.success('已标注为已完成');
  };
  const openUnableReasonModal = () => {
    const reason = String(processUnableReason || '').trim();
    const quickMatch = reason === '库存不足' || reason === '指令错误' ? reason : '';
    setUnableReasonQuickValue(quickMatch);
    setUnableReasonInput(reason || quickMatch);
    setUnableReasonModalOpen(true);
  };
  const submitUnableReasonModal = () => {
    const finalReason = String(unableReasonInput || unableReasonQuickValue || '').trim();
    if (!finalReason) return void message.warning('请填写无法操作原因');
    setProcessResultStatus('unable');
    setProcessUnableReason(finalReason);
    setUnableReasonModalOpen(false);
    try {
      onChange?.('process_result_status', 'unable');
      onChange?.('process_unable_reason', finalReason);
    } catch {}
    appendOperationRecord('标注无法操作', finalReason);
    message.success('已标注为无法操作');
  };
  const printOtherAttachmentFromList = async (att: any) => {
    await openAttachmentPreview(att);
    appendOperationRecord('打印其他附件', String(att?.file_name || att?.name || att?.file || att?.file_path || '-'));
    setTimeout(() => {
      closePreviewAfterPrintRef.current = true;
      printPreview();
      // 兜底：部分浏览器不触发 afterprint
      setTimeout(() => {
        if (!closePreviewAfterPrintRef.current) return;
        closePreviewAfterPrintRef.current = false;
        closePreview();
      }, 8000);
    }, 120);
  };

  const printableAttachments = useMemo(() => {
    const arr = Array.isArray(instructionDetail?.attachments_detail) ? instructionDetail.attachments_detail : [];
    // “其他附件”应展示所有非运单附件；是否需打印仅影响图标/提示，不应影响展示。
    return arr.filter((a:any) => String(a?.label || '').trim() !== '物流运单');
  }, [instructionDetail?.attachments_detail]);
  const printableAttachmentsWithPrintRequirement = useMemo(() => {
    const arr = Array.isArray(instructionDetail?.attachments_detail) ? instructionDetail.attachments_detail : [];
    return arr.filter((a:any) => hasPrintableRequirement(a));
  }, [instructionDetail?.attachments_detail]);

  useEffect(() => {
    (async () => {
      try {
        const rows = Array.isArray(instructionDetail?.targets_detail) ? instructionDetail.targets_detail : [];
        const pendingIds = new Set<string>();
        rows.forEach((r: any) => {
          const has = extractUnitContentItems(r);
          const uid = r?.target?.inventory_unit_id ?? r?.inventory_unit_id;
          if (uid != null && (!has || has.length === 0) && !unitContentCache[String(uid)]) pendingIds.add(String(uid));
        });
        if (pendingIds.size === 0) return;
        const updates: Record<string, Array<{ barcode: string; qty: number }>> = {};
        const warehouseId = instructionDetail?.warehouse_id || instructionDetail?.instruction_warehouse_id || undefined;
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(Number(id), warehouseId ? { warehouse_id: warehouseId } : undefined);
            const d: any = (resp as any)?.data || resp;
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            const mapped = (arr || []).map((it: any) => {
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) barcode = String(it?.product_system_code || it?.system_code || it?.product_code || '');
              return { barcode, qty };
            }).filter((x: any) => x.barcode || x.qty);
            if (mapped.length > 0) updates[id] = mapped;
          } catch {}
        }));
        if (Object.keys(updates).length > 0) setUnitContentCache(prev => ({ ...prev, ...updates }));
      } catch {}
    })();
  }, [instructionDetail?.targets_detail, instructionDetail?.warehouse_id, instructionDetail?.instruction_warehouse_id, unitContentCache]);

  const appendOperationRecord = (action: string, remark?: string) => {
    const t = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts = `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())} ${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
    const rec = { time: ts, action: String(action || '-'), remark: String(remark || '-').trim() || '-' };
    setLocalOperationRecords((prev) => {
      const next = [rec, ...(Array.isArray(prev) ? prev : [])].slice(0, 100);
      try { onChange?.('operation_records', next); } catch {}
      return next;
    });
  };
  const clearOperationRecords = () => {
    if (!canOperate) return;
    setLocalOperationRecords([]);
    try { onChange?.('operation_records', []); } catch {}
    message.success('操作记录已清空');
  };
  const operationRecords = useMemo(() => {
    try {
      const arr = Array.isArray(localOperationRecords) ? localOperationRecords : [];
      return arr.slice(0, 20).map((r:any, idx:number) => ({
        key: `${idx}-${String(r?.time || '')}`,
        time: String(r?.time || '-'),
        action: String(r?.action || '-'),
        remark: String(r?.remark || '-'),
      }));
    } catch {
      return [];
    }
  }, [localOperationRecords]);
  useEffect(() => {
    const total = Array.isArray(operationRecords) ? operationRecords.length : 0;
    const maxPage = Math.max(1, Math.ceil(total / opRecordPageSize));
    if (opRecordPage > maxPage) setOpRecordPage(maxPage);
  }, [operationRecords, opRecordPage, opRecordPageSize]);

  const normalizeCode = (v: any) => String(v ?? '').trim().toUpperCase();
  const getInventoryPickKey = (r: any) => normalizeCode(r?.target_label || r?.key || '');
  const playWarningBeep = async () => {
    try {
      const Ctx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (Ctx) {
        if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended') {
          try { await ctx.resume(); } catch {}
        }
        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.26);

        const osc1 = ctx.createOscillator();
        osc1.type = 'square';
        osc1.frequency.setValueAtTime(920, now);
        osc1.connect(gain);
        osc1.start(now);
        osc1.stop(now + 0.12);

        const osc2 = ctx.createOscillator();
        osc2.type = 'square';
        osc2.frequency.setValueAtTime(760, now + 0.14);
        osc2.connect(gain);
        osc2.start(now + 0.14);
        osc2.stop(now + 0.26);
        audioUnlockedRef.current = true;
        return;
      }
    } catch {}
    // WebAudio 不可用时兜底：尝试系统提示音（部分浏览器可能静默）
    try {
      const a = new Audio('data:audio/wav;base64,UklGRmQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YUAAAAA=');
      a.volume = 1;
      await a.play().catch(() => {});
    } catch {}
  };
  const getTargetQty = (row: any) => Math.max(0, Number(row?.target_qty || 0));
  const getSortingBarcodeSet = (r: any): Set<string> => {
    const raw = String(r?.sorting_barcode || '').trim();
    if (!raw) return new Set();
    return new Set(
      raw
        .split(/[、,，\/|\s]+/g)
        .map((x) => normalizeCode(x))
        .filter(Boolean),
    );
  };
  const getVerifiedQty = (row: any, m: Record<string, number>) => {
    const key = getInventoryPickKey(row);
    if (!key) return 0;
    const n = Number(m?.[key] || 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const isRowReached = (row: any, m: Record<string, number>) => {
    const target = getTargetQty(row);
    if (target <= 0) return false;
    return getVerifiedQty(row, m as any) >= target;
  };
  const pickVerifyModalStats = useMemo(() => {
    const total = Array.isArray(inventoryRows) ? inventoryRows.length : 0;
    const done = (inventoryRows || []).filter((r: any) => !!pickVerifyTempMap[getInventoryPickKey(r)]).length;
    return { total, done, pending: Math.max(0, total - done) };
  }, [inventoryRows, pickVerifyTempMap]);

  const openPickVerifyModal = () => {
    // 每次进入分拣验证都重新开始：清空已分拣数量
    setPickVerifyMap({});
    setPickVerifyTempMap({});
    try { onChange?.('pick_verify', {}); } catch {}
    setPickScanInput('');
    setPickVerifyOpen(true);
    setTimeout(() => {
      try { pickScanInputRef.current?.focus?.(); } catch {}
    }, 0);
  };
  const applyPickVerifyScan = () => {
    const code = normalizeCode(pickScanInput);
    if (!code) {
      message.warning('请先输入分拣条码或库存单元系统编码');
      playWarningBeep();
      return;
    }
    const row = (inventoryRows || []).find((r: any) => {
      const iuCode = normalizeCode(r?.target_label);
      if (iuCode === code) return true;
      const sbSet = getSortingBarcodeSet(r);
      return sbSet.has(code);
    });
    if (!row) {
      message.warning('未匹配到当前指令的库存单元/分拣条码');
      playWarningBeep();
      setPickScanInput('');
      setTimeout(() => {
        try { pickScanInputRef.current?.focus?.(); } catch {}
      }, 0);
      return;
    }
    const key = getInventoryPickKey(row);
    if (!key) {
      message.warning('无有效库存单元编码');
      playWarningBeep();
      return;
    }
    const targetQty = getTargetQty(row);
    const currQty = Number(pickVerifyTempMap[key] || 0);
    if (targetQty > 0 && currQty >= targetQty) {
      message.warning('该库存单元分拣数量已达到目标数量');
      playWarningBeep();
      setPickScanInput('');
      setTimeout(() => {
        try { pickScanInputRef.current?.focus?.(); } catch {}
      }, 0);
      return;
    }
    const nextQty = currQty + 1;
    setPickVerifyTempMap((prev) => ({ ...prev, [key]: nextQty }));
    setPickScanInput('');
    setTimeout(() => {
      try { pickScanInputRef.current?.focus?.(); } catch {}
    }, 0);
    message.success(`分拣验证成功：${key}（${nextQty}/${targetQty || '-' }）`);
  };
  const confirmPickVerify = () => {
    setPickVerifyMap({ ...(pickVerifyTempMap || {}) });
    try { onChange?.('pick_verify', { ...(pickVerifyTempMap || {}) }); } catch {}
    const done = (inventoryRows || []).filter((r: any) => {
      const key = getInventoryPickKey(r);
      return Number(pickVerifyTempMap?.[key] || 0) > 0;
    }).length;
    appendOperationRecord('分拣验证', `已分拣 ${done}/${inventoryRows.length || 0}`);
    setPickVerifyOpen(false);
  };
  const createInstructionOutputContainer = async () => {
    if (!canOperate) return;
    if (waybillStatusText !== '先申请包装数据') return void message.warning('仅“先申请包装数据”场景可创建产出容器');
    if (!savedPackData) return void message.warning('请先输入包装数据');
    if (createIocSubmitting) return;
    const instructionId = Number((instructionDetail as any)?.id || 0);
    if (!instructionId) return void message.warning('缺少指令ID');
    const qtyByIu: Record<number, number> = {};
    const targets = Array.isArray(instructionDetail?.targets_detail) ? instructionDetail.targets_detail : [];
    for (const t of targets) {
      const iuId = Number(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? 0);
      const qty = Math.floor(Number(t?.expected_qty ?? t?.target_qty ?? 0) || 0);
      if (!Number.isFinite(iuId) || iuId <= 0 || !Number.isFinite(qty) || qty <= 0) continue;
      qtyByIu[iuId] = Number(qtyByIu[iuId] || 0) + qty;
    }
    if (Object.keys(qtyByIu).length === 0) {
      // 兜底：若 targets_detail 缺失，尝试从前端列表聚合。
      for (const r of (inventoryRows || [])) {
        const iuId = Number(r?.inventory_unit_id || 0);
        const qty = Math.floor(Number(r?.target_qty || 0) || 0);
        if (!Number.isFinite(iuId) || iuId <= 0 || !Number.isFinite(qty) || qty <= 0) continue;
        qtyByIu[iuId] = Number(qtyByIu[iuId] || 0) + qty;
      }
    }
    const items = Object.entries(qtyByIu).map(([iuId, qty]) => ({
      inventory_unit_id: Number(iuId),
      quantity: Number(qty),
    })).filter((x) => Number.isFinite(x.inventory_unit_id) && x.inventory_unit_id > 0 && Number.isFinite(x.quantity) && x.quantity > 0);
    if (items.length === 0) return void message.warning('缺少可写入IOC的库存单元与目标数量');
    setCreateIocSubmitting(true);
    try {
      let code = String(createdIocCode || '').trim();
      const updatingExisting = !!code;
      if (!code) {
        const reserveResp = await request.post('/api/v1/storage/output-containers/reserve-code/', {
          instruction_id: instructionId,
          container_type: 'box',
        });
        code = String((reserveResp as any)?.data?.code || '').trim();
        if (!code) throw new Error('预生成IOC编码失败');
      }
      const finalizePayload: any = {
        instruction_id: instructionId,
        code,
        container_type: 'box',
        status: 'closed',
        execution_session_id: String(executionSessionId || '').trim() || undefined,
        length_cm: Math.round(Number(savedPackData.lengthCm || 0)),
        width_cm: Math.round(Number(savedPackData.widthCm || 0)),
        height_cm: Math.round(Number(savedPackData.heightCm || 0)),
        weight_g: Math.round(Number(savedPackData.weightG || 0)),
        defer_inventory_settlement: true,
      };
      // 更新既有 IOC 时，仅更新物理数据，不覆盖容器内容物。
      if (!updatingExisting) finalizePayload.items = items;
      await request.post('/api/v1/storage/output-containers/finalize/', finalizePayload);
      // 方案A：IOC 成功后追加回写指令“实际装箱数据”（不改变既有 IOC 流程）
      const patchPayload = {
        actual_pack_length_cm: Math.round(Number(savedPackData.lengthCm || 0)),
        actual_pack_width_cm: Math.round(Number(savedPackData.widthCm || 0)),
        actual_pack_height_cm: Math.round(Number(savedPackData.heightCm || 0)),
        actual_gross_weight_g: Math.round(Number(savedPackData.weightG || 0)),
        pack_result_status: 'provided',
      };
      try {
        await request.patch(`/api/v1/instruction/instructions/${instructionId}/`, patchPayload);
      } catch (e: any) {
        // IOC 已创建成功，指令字段回写失败仅提示，不回滚 IOC。
        message.warning(e?.response?.data?.detail || '已创建IOC，但回写指令实际装箱数据失败');
      }
      const totalQty = items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
      setCreatedIocCode(code);
      try {
        onChange?.('created_ioc_code', code);
        if (!updatingExisting) onChange?.('created_ioc_items', items);
        onChange?.('actual_pack_length_cm', patchPayload.actual_pack_length_cm);
        onChange?.('actual_pack_width_cm', patchPayload.actual_pack_width_cm);
        onChange?.('actual_pack_height_cm', patchPayload.actual_pack_height_cm);
        onChange?.('actual_gross_weight_g', patchPayload.actual_gross_weight_g);
        onChange?.('pack_result_status', 'provided');
      } catch {}
      appendOperationRecord(
        updatingExisting ? '更新指令产出容器' : '创建指令产出容器',
        updatingExisting ? `${code}（仅更新物理数据）` : `${code}（${items.length}种/${totalQty}件）`,
      );
      message.success(updatingExisting ? `已更新指令产出容器：${code}` : `已创建指令产出容器：${code}`);
      await previewAndPrintIocLabel(code, totalQty, items.length);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '创建指令产出容器失败');
    } finally {
      setCreateIocSubmitting(false);
    }
  };

  const disableInputPackagingBtn = !canOperate;
  const disableBuyWaybillBtn = !canOperate || waybillStatusText === '有运单' || waybillStatusText === '先申请包装数据';
  const disablePrintWaybillBtn = !canOperate || waybillAttachments.length === 0;
  const disablePrintOtherAttachmentsBtn = !canOperate || printableAttachmentsWithPrintRequirement.length === 0;
  const disableCreateIocBtn = !canOperate || waybillStatusText !== '先申请包装数据' || !savedPackData || createIocSubmitting;
  const waybillSourceRaw = String(
    instructionDetail?.waybill_source
    || instructionDetail?.customer_answers?.waybill_source
    || '',
  ).trim().toLowerCase();
  const waybillModeRaw = String(
    instructionDetail?.waybill_submit_mode
    || instructionDetail?.customer_answers?.waybill_submit_mode
    || '',
  ).trim().toLowerCase();
  const isAfterPackInstruction = waybillSourceRaw === 'upload' && waybillModeRaw === 'after_pack';
  const isWarehouseProvideWaybillInstruction = waybillSourceRaw === 'warehouse';
  const hasUploadedWaybillCore = Boolean(
    waybillAttachments.length > 0
    && sanitizeNullableText(localWaybillCarrier)
    && sanitizeNullableText(localWaybillTrackingNo),
  );
  const hasCreatedInstructionOutputContainer = Boolean(String(createdIocCode || '').trim());
  const disableMarkCompletedBtn = !canOperate
    || (isAfterPackInstruction && (!savedPackData || !hasCreatedInstructionOutputContainer))
    || (isWarehouseProvideWaybillInstruction && !hasUploadedWaybillCore);
  const markCompletedDisableReason = (() => {
    if (!canOperate) return '当前不可操作';
    if (isAfterPackInstruction && (!savedPackData || !hasCreatedInstructionOutputContainer)) {
      return '请先输入包装数据并创建指令产出容器';
    }
    if (isWarehouseProvideWaybillInstruction && !hasUploadedWaybillCore) {
      return '请先购买并上传运单';
    }
    return '';
  })();
  const waybillChargeText = useMemo(() => {
    const amountRaw = String(localWaybillChargeAmount || '').trim();
    if (!amountRaw) return '-';
    const currency = String(localWaybillChargeCurrency || '').trim().toUpperCase();
    return currency ? `${amountRaw} ${currency}` : amountRaw;
  }, [localWaybillChargeAmount, localWaybillChargeCurrency]);
  const recipientAuxInfo = useMemo(() => {
    const info = (instructionDetail as any)?.buy_waybill_info || {};
    const answers = (instructionDetail as any)?.customer_answers || {};
    const pickText = (v: any, fallback: any = '') => sanitizeNullableText(v) || sanitizeNullableText(fallback);
    const joinNonEmpty = (arr: any[], sep: string) => arr.map((v) => sanitizeNullableText(v)).filter(Boolean).join(sep);
    const parseBool = (v: any): boolean | null => {
      if (v === true || v === false) return v;
      const s = String(v ?? '').trim().toLowerCase();
      if (!s) return null;
      if (['1', 'true', 'yes', 'y', '是', '需要'].includes(s)) return true;
      if (['0', 'false', 'no', 'n', '否', '不需要'].includes(s)) return false;
      return null;
    };
    const recipient = info?.recipient || {};
    const line1 = pickText(recipient?.name, answers?.recipient_name);
    const line2 = joinNonEmpty([recipient?.address_line_1 ?? answers?.address_line_1, recipient?.address_line_2 ?? answers?.address_line_2], ', ');
    const line3 = joinNonEmpty([recipient?.city ?? answers?.city, recipient?.state ?? answers?.state, recipient?.postal_code ?? answers?.postal_code], ' ');
    const line4 = joinNonEmpty([
      recipient?.company_name ?? recipient?.company ?? answers?.company_name,
      recipient?.phone ?? answers?.phone,
      recipient?.email ?? answers?.email,
      recipient?.country ?? answers?.country,
    ], ' ');
    const insuranceRequired = parseBool(info?.insurance_signature?.insurance_required ?? answers?.insurance_required);
    const signatureRequired = parseBool(info?.insurance_signature?.signature_required ?? answers?.signature_required);
    const insuredAmount = info?.insurance_signature?.insured_amount ?? answers?.insured_amount;
    const insuranceInfo = insuranceRequired === true
      ? `需要${insuredAmount != null ? `（$${insuredAmount}）` : ''}`
      : insuranceRequired === false
      ? '不需要'
      : '';
    const signatureInfo = signatureRequired === true ? '需要' : signatureRequired === false ? '不需要' : '';
    const copyText = [line1, line2, line3].filter(Boolean).join('\n');
    return {
      line1: line1 || '-',
      line2: line2 || '-',
      line3: line3 || '-',
      line4: line4 || '-',
      copyText,
      preferredCarrierText: pickText(info?.preferred_carrier, pickText(answers?.preferred_carrier, pickText(localWaybillCarrier))) || '-',
      insuranceInfo: insuranceInfo || '-',
      insuranceRequired,
      signatureInfo: signatureInfo || '-',
      signatureRequired,
      buyerRemarksText: pickText(info?.buyer_remarks, pickText(answers?.shipping_notes)) || '-',
    };
  }, [instructionDetail, localWaybillCarrier]);

  return (
    <div style={{ width: '100%', display: 'flex', gap: 12 }}>
      <div style={{ flex: '0 0 60%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card
          size="small"
          title="指令概览"
          bodyStyle={{ padding: 12 }}
          style={{ border: '2px solid #91caff', borderRadius: 8 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <span style={{ whiteSpace: 'nowrap' }}>指令系统编码：{normalizeText(instructionDetail?.system_code || instructionCodeFromParent || '-')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 30, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
              <span>处理结果：</span>
              {processResultStatus === 'completed' ? (
                <Button size="small" style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}>已完成</Button>
              ) : processResultStatus === 'unable' ? (
                <>
                  <Button size="small" style={{ background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}>无法操作</Button>
                  <span>{`(原因:${processUnableReason || '-'})`}</span>
                </>
              ) : (
                <span>未标注</span>
              )}
            </span>
          </div>
          <div>
            执行提醒：
            <span style={{ color: '#cf1322' }}>{normalizeText(instructionDetail?.execution_tips || '-')}</span>
          </div>
          <div>指令备注信息：{normalizeText(instructionDetail?.remarks || '-')}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <span style={{ whiteSpace: 'nowrap' }}>其他附件：</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {printableAttachments.length === 0 ? (
                <span>-</span>
              ) : printableAttachments.map((a: any, idx: number) => {
                const name = normalizeText(a?.file_name || a?.name || a?.file || a?.file_path || `附件${idx + 1}`);
                const printText = getAttachmentPrintText(a);
                const printState = getAttachmentPrintState(a);
                return (
                  <div key={String(a?.id ?? idx)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => { void openAttachmentPreview(a); }}>
                      {name}
                    </Button>
                    {printState === 'print' ? (
                      <>
                        <Tooltip title={printText}>
                          <PrinterOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
                        </Tooltip>
                        <span style={{ color: '#cf1322' }}>(需打印)</span>
                      </>
                    ) : null}
                    {printState === 'no_print' ? (
                      <Tooltip title={printText}>
                        <span style={{ color: '#8c8c8c' }}>(明确无需打印)</span>
                      </Tooltip>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        <Card
          size="small"
          title="库存单元信息"
          bodyStyle={{ padding: 12 }}
          style={{ border: '2px solid #b7eb8f', borderRadius: 8 }}
        >
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            locale={{ emptyText: '暂无库存单元信息' }}
            dataSource={inventoryRows}
            columns={[
              { title: '项目', dataIndex: 'project', key: 'project', width: 220 },
              { title: '库存单元', dataIndex: 'target_label', key: 'target_label', width: 220 },
              { title: '分拣条码', dataIndex: 'sorting_barcode', key: 'sorting_barcode' },
              { title: '目标数量', dataIndex: 'target_qty', key: 'target_qty', width: 120, align: 'center' as const },
              {
                title: '已分拣',
                key: 'pick_verified',
                width: 100,
                align: 'center' as const,
                render: (_: any, row: any) => getVerifiedQty(row, pickVerifyMap) || '',
              },
            ] as any}
            onRow={(row: any) => (isRowReached(row, pickVerifyMap) ? { style: { background: '#f6ffed' } } : {})}
          />
        </Card>

        <Card
          size="small"
          title="包装与运单信息"
          bodyStyle={{ padding: 12 }}
          style={{ border: '2px solid #ffd591', borderRadius: 8 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                是否需打包：
                {instructionDetail?.is_pack_required === true ? (
                  <Button size="small" danger style={{ marginLeft: 8 }}>需打包</Button>
                ) : instructionDetail?.is_pack_required === false ? (
                  <Button size="small" style={{ marginLeft: 8, background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}>无需打包</Button>
                ) : '-'}
              </div>
              <div>
                打包方式和推荐包材：
                <span>{packMethodText}</span>
                {recommendLoading ? <span style={{ color: '#1677ff' }}> | 推荐包材计算中...</span> : null}
                {!recommendLoading && (recommendedPackDisplay.code || recommendedPackDisplay.name) ? (
                  <span>
                    {' | '}
                    <span style={{ color: '#cf1322', fontWeight: 600 }}>{recommendedPackDisplay.code || '-'}</span>
                    {recommendedPackDisplay.name ? ` · ${recommendedPackDisplay.name}` : ''}
                  </span>
                ) : null}
              </div>
            </div>

            <div>
              包装数据：{packagingDataNode}
            </div>

            <div>
              已创建指令产出容器：
              {createdIocCode ? (
                <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#1677ff', fontWeight: 600 }}>{createdIocCode}</span>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => { void previewAndPrintIocLabel(createdIocCode); }}
                  >
                    打印标签
                  </Button>
                </span>
              ) : (
                <span style={{ marginLeft: 8 }}>-</span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 20 }}>
              <div>
                运单状态：
                {waybillStatusText === '有运单' ? (
                  <Button size="small" style={{ marginLeft: 8, background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}>有运单</Button>
                ) : waybillStatusText === '仓库提供运单' ? (
                  <Button size="small" danger style={{ marginLeft: 8 }}>仓库提供运单</Button>
                ) : waybillStatusText === '先申请包装数据' ? (
                  <Button size="small" style={{ marginLeft: 8, background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}>先申请包装数据</Button>
                ) : (
                  <span style={{ marginLeft: 8 }}>{waybillStatusText}</span>
                )}
              </div>
              <div>
                运单附件：
                {waybillAttachments.length > 0 ? (
                  <span style={{ marginLeft: 8, display: 'inline-flex', flexDirection: 'column', gap: 2, verticalAlign: 'top' }}>
                    {waybillAttachments.map((a: any, idx: number) => (
                      <span key={String(a?.id ?? idx)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span>{normalizeText(a?.file_name || a?.name || a?.file || a?.file_path || `运单附件${idx + 1}`)}</span>
                        <Button
                          size="small"
                          type="link"
                          style={{ paddingInline: 0 }}
                          onClick={() => { void openAttachmentPreview(a); }}
                        >
                          查看
                        </Button>
                        <Button
                          size="small"
                          type="link"
                          danger
                          style={{ paddingInline: 0 }}
                          disabled={!canOperate}
                          onClick={() => { void deleteWaybillAttachment(a); }}
                        >
                          删除
                        </Button>
                      </span>
                    ))}
                  </span>
                ) : (
                  <span style={{ marginLeft: 8 }}>{waybillAttachmentText}</span>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>承运商：{normalizeText(localWaybillCarrier || '-')}</div>
              <div>物流跟踪号：{normalizeText(localWaybillTrackingNo || '-')}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>运单金额：{waybillChargeText}</div>
              <div>费用备注：{localWaybillChargeNote || '-'}</div>
            </div>
            <Collapse
              size="small"
              ghost
              items={[
                {
                  key: 'recipient_aux_info',
                  label: '收件人与辅助信息（点击展开）',
                  children: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ border: '1px solid #52c41a', borderRadius: 6, padding: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div style={{ fontWeight: 600, color: '#389e0d' }}>收件人完整信息</div>
                          <Button
                            size="small"
                            onClick={async () => {
                              if (!recipientAuxInfo.copyText) return void message.warning('无可复制信息');
                              try {
                                await navigator.clipboard.writeText(recipientAuxInfo.copyText);
                                message.success('已复制');
                              } catch {
                                message.error('复制失败');
                              }
                            }}
                          >
                            复制信息
                          </Button>
                        </div>
                        <div>{recipientAuxInfo.line1}</div>
                        <div>{recipientAuxInfo.line2}</div>
                        <div>{recipientAuxInfo.line3}</div>
                        <div>{recipientAuxInfo.line4}</div>
                      </div>
                      <div style={{ border: '1px solid #1677ff', borderRadius: 6, padding: 10 }}>
                        <div style={{ fontWeight: 600, color: '#1677ff', marginBottom: 6 }}>辅助信息</div>
                        <div>偏好承运商：{recipientAuxInfo.preferredCarrierText}</div>
                        <div>
                          保险信息：
                          {recipientAuxInfo.insuranceRequired === true ? (
                            <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                              {recipientAuxInfo.insuranceInfo}
                            </span>
                          ) : (
                            <span>{recipientAuxInfo.insuranceInfo}</span>
                          )}
                        </div>
                        <div>
                          签名需求：
                          {recipientAuxInfo.signatureRequired === true ? (
                            <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                              {recipientAuxInfo.signatureInfo}
                            </span>
                          ) : (
                            <span>{recipientAuxInfo.signatureInfo}</span>
                          )}
                        </div>
                        <div>买家备注：{recipientAuxInfo.buyerRemarksText}</div>
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Card>
      </div>

      <div style={{ flex: '0 0 40%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card
          size="small"
          title="操作区域"
          bodyStyle={{ padding: 12 }}
          style={{ border: '2px solid #ffadd2', borderRadius: 8 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Button block size="small" disabled={!canOperate} onClick={openPickVerifyModal}>分拣验证</Button>
              <Button block size="small" disabled={disablePrintOtherAttachmentsBtn} onClick={openPrintOtherAttachmentsModal}>打印其他附件</Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Button block size="small" disabled={disableInputPackagingBtn} onClick={openPackDataModal}>输入包装数据</Button>
              <Tooltip title={createdIocCode ? `当前产出容器：${createdIocCode}（再次点击将更新该容器的物理数据,不会更新容器内容物信息）` : '仅用于"先申请包装数据"场景,需先输入包装数据再创建产出容器.'}>
                <span style={{ display: 'block' }}>
                  <Button block size="small" loading={createIocSubmitting} disabled={disableCreateIocBtn} onClick={() => { void createInstructionOutputContainer(); }}>
                    {createdIocCode ? '更新指令产出容器' : '创建指令产出容器'}
                  </Button>
                </span>
              </Tooltip>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Button block size="small" disabled={disableBuyWaybillBtn} onClick={openBuyWaybillModal}>购买并上传运单</Button>
              <Button block size="small" disabled={disablePrintWaybillBtn} onClick={() => { void printWaybillDirectly(); }}>打印运单</Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Tooltip title={disableMarkCompletedBtn ? markCompletedDisableReason : ''}>
                <span style={{ display: 'block' }}>
                  <Button block size="small" type="primary" disabled={disableMarkCompletedBtn} onClick={markProcessCompleted}>标注已完成</Button>
                </span>
              </Tooltip>
              <Button block size="small" danger disabled={!canOperate} onClick={openUnableReasonModal}>标注无法操作</Button>
            </div>
          </div>
        </Card>

        <Card
          size="small"
          title="操作记录"
          extra={(
            <Button size="small" onClick={clearOperationRecords} disabled={!canOperate || operationRecords.length === 0}>
              清空
            </Button>
          )}
          bodyStyle={{ padding: 12 }}
          style={{ border: '2px solid #d3adf7', borderRadius: 8 }}
        >
          <Table
            size="small"
            rowKey="key"
            pagination={{
              current: opRecordPage,
              pageSize: opRecordPageSize,
              total: operationRecords.length,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
              onChange: (page, pageSize) => {
                setOpRecordPage(page);
                if (pageSize !== opRecordPageSize) setOpRecordPageSize(pageSize);
              },
            }}
            locale={{ emptyText: '暂无操作记录' }}
            dataSource={operationRecords}
            columns={[
              { title: '时间', dataIndex: 'time', key: 'time', width: 140 },
              { title: '操作', dataIndex: 'action', key: 'action', width: 100 },
              { title: '备注', dataIndex: 'remark', key: 'remark' },
            ] as any}
          />
        </Card>
      </div>
      <Modal
        open={unableReasonModalOpen}
        title="标注无法操作原因"
        onCancel={() => setUnableReasonModalOpen(false)}
        onOk={submitUnableReasonModal}
        okText="确认标注"
        cancelText="取消"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 6 }}>快速选择</div>
            <Radio.Group
              value={unableReasonQuickValue || undefined}
              onChange={(e) => {
                const v = String(e?.target?.value || '');
                setUnableReasonQuickValue(v);
                setUnableReasonInput(v);
              }}
            >
              <Radio.Button value="库存不足">库存不足</Radio.Button>
              <Radio.Button value="指令错误">指令错误</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>原因说明（可编辑）</div>
            <Input.TextArea
              rows={3}
              value={unableReasonInput}
              onChange={(e) => {
                const v = String(e.target.value || '');
                setUnableReasonInput(v);
                if (v !== unableReasonQuickValue) setUnableReasonQuickValue('');
              }}
              placeholder="请输入无法操作原因"
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={printOtherAttachmentsModalOpen}
        title="打印其他附件"
        onCancel={() => setPrintOtherAttachmentsModalOpen(false)}
        footer={null}
        width={760}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {printableAttachmentsWithPrintRequirement.length === 0 ? (
            <div style={{ color: '#8c8c8c' }}>暂无有打印需求的其他附件</div>
          ) : printableAttachmentsWithPrintRequirement.map((a: any, idx: number) => {
            const name = normalizeText(a?.file_name || a?.name || a?.file || a?.file_path || `附件${idx + 1}`);
            const printText = getAttachmentPrintText(a) || '未配置打印设置';
            return (
              <div
                key={String(a?.id ?? idx)}
                style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, wordBreak: 'break-all' }}>{name}</div>
                  <div style={{ color: '#cf1322', marginTop: 4, wordBreak: 'break-all' }}>{printText}</div>
                </div>
                <Button type="primary" size="small" onClick={() => { void printOtherAttachmentFromList(a); }}>
                  预览并打印
                </Button>
              </div>
            );
          })}
        </div>
      </Modal>
      <Modal
        open={packDataModalOpen}
        title="输入包装数据"
        onCancel={() => setPackDataModalOpen(false)}
        onOk={submitPackDataModal}
        okText="确认"
        cancelText="取消"
      >
        <div style={{ marginBottom: 10 }}>
          <span style={{ color: '#8c8c8c', marginRight: 8 }}>打包方式和推荐包材：</span>
          <span>{packMethodText}</span>
          {recommendLoading ? <span style={{ color: '#1677ff' }}> | 推荐包材计算中...</span> : null}
          {!recommendLoading && (recommendedPackDisplay.code || recommendedPackDisplay.name) ? (
            <span>
              {' | '}
              <span style={{ color: '#cf1322', fontWeight: 600 }}>{recommendedPackDisplay.code || '-'}</span>
              {recommendedPackDisplay.name ? ` · ${recommendedPackDisplay.name}` : ''}
            </span>
          ) : null}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 6 }}>长(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={packDataLengthCm ?? undefined}
              onChange={(v) => setPackDataLengthCm(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>宽(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={packDataWidthCm ?? undefined}
              onChange={(v) => setPackDataWidthCm(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>高(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={packDataHeightCm ?? undefined}
              onChange={(v) => setPackDataHeightCm(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>重量(g)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={packDataWeightG ?? undefined}
              onChange={(v) => setPackDataWeightG(v == null ? null : Number(v))}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={buyWaybillModalOpen}
        title={(() => {
          const info = (instructionDetail as any)?.buy_waybill_info || {};
          const code = String(instructionDetail?.system_code || instructionCodeFromParent || '-').trim() || '-';
          const answers = (instructionDetail as any)?.customer_answers || {};
          const preferredCarrier = sanitizeNullableText(info?.preferred_carrier)
            || sanitizeNullableText(answers?.preferred_carrier)
            || sanitizeNullableText(localWaybillCarrier);
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span>购买并上传运单信息</span>
              <span style={{ color: '#1677ff', fontWeight: 600 }}>{code}</span>
              <span style={{ marginLeft: 12 }}>
                偏好承运商: <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{preferredCarrier || '无'}</span>
              </span>
            </div>
          );
        })()}
        onCancel={() => setBuyWaybillModalOpen(false)}
        onOk={submitBuyWaybillUpload}
        okText="提交并更新指令"
        cancelText="取消"
        confirmLoading={buyWaybillSubmitting}
        width={900}
      >
        {(() => {
          const info = (instructionDetail as any)?.buy_waybill_info || {};
          const answers = (instructionDetail as any)?.customer_answers || {};
          const recipient = info?.recipient || {};
          const normText = (v: any): string => {
            const s = String(v ?? '').trim();
            if (!s) return '';
            const low = s.toLowerCase();
            if (low === 'none' || low === 'null' || low === 'undefined' || s === '-') return '';
            return s;
          };
          const pickText = (v: any, fallback: any = '') => {
            const s = normText(v);
            if (s) return s;
            return normText(fallback);
          };
          const joinNonEmpty = (arr: any[], sep: string, fallback: string = '') => {
            const vals = arr.map((v) => normText(v)).filter(Boolean);
            return vals.length > 0 ? vals.join(sep) : fallback;
          };
          const parseBool = (v: any): boolean | null => {
            if (v === true || v === false) return v;
            const s = String(v ?? '').trim().toLowerCase();
            if (!s) return null;
            if (['1', 'true', 'yes', 'y', '是', '需要'].includes(s)) return true;
            if (['0', 'false', 'no', 'n', '否', '不需要'].includes(s)) return false;
            return null;
          };
          const insuranceRequiredRaw = info?.insurance_signature?.insurance_required ?? answers?.insurance_required;
          const signatureRequiredRaw = info?.insurance_signature?.signature_required ?? answers?.signature_required;
          const insuranceRequired = parseBool(insuranceRequiredRaw);
          const signatureRequired = parseBool(signatureRequiredRaw);
          const insuredAmount = info?.insurance_signature?.insured_amount ?? answers?.insured_amount;
          const insuranceInfo = insuranceRequired === true
            ? `需要${insuredAmount != null ? `（$${insuredAmount}）` : ''}`
            : insuranceRequired === false
            ? '不需要'
            : '';
          const signatureInfo = signatureRequired === true ? '需要' : signatureRequired === false ? '不需要' : '';
          const line1 = pickText(recipient?.name, answers?.recipient_name);
          const line2 = joinNonEmpty(
            [recipient?.address_line_1 ?? answers?.address_line_1, recipient?.address_line_2 ?? answers?.address_line_2],
            ', ',
          );
          const line3 = joinNonEmpty(
            [recipient?.city ?? answers?.city, recipient?.state ?? answers?.state, recipient?.postal_code ?? answers?.postal_code],
            ' ',
          );
          const line4 = joinNonEmpty(
            [
              recipient?.company_name ?? recipient?.company ?? answers?.company_name,
              recipient?.phone ?? answers?.phone,
              recipient?.email ?? answers?.email,
              recipient?.country ?? answers?.country,
            ],
            ' ',
          );
          const copyText = [line1, line2, line3].filter(Boolean).join('\n');
          const preferredCarrierText = pickText(info?.preferred_carrier, pickText(answers?.preferred_carrier, pickText(localWaybillCarrier)));
          const buyerRemarksText = pickText(info?.buyer_remarks, pickText(answers?.shipping_notes));
          const instructionRemarksText = pickText(info?.instruction_remarks, instructionDetail?.remarks);
          const executionTipsText = pickText(info?.execution_tips, instructionDetail?.execution_tips);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, lineHeight: 1.6 }}>
              {savedPackData ? (
                <div style={{ border: '1px solid #fa8c16', borderRadius: 6, padding: 12 }}>
                  <div style={{ fontWeight: 600, color: '#d46b08', marginBottom: 6 }}>包装数据</div>
                  <div>{packagingDataNode}</div>
                </div>
              ) : null}
              <div style={{ border: '1px solid #52c41a', borderRadius: 6, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, color: '#389e0d' }}>收件人完整信息</div>
                  <Button
                    size="small"
                    onClick={async () => {
                      if (!copyText) return void message.warning('无可复制信息');
                      try {
                        await navigator.clipboard.writeText(copyText);
                        message.success('已复制');
                      } catch {
                        message.error('复制失败');
                      }
                    }}
                  >
                    复制信息
                  </Button>
                </div>
                <div>{line1 || '-'}</div>
                <div>{line2 || '-'}</div>
                <div>{line3 || '-'}</div>
                <div>{line4 || '-'}</div>
              </div>
              <div style={{ border: '1px solid #1677ff', borderRadius: 6, padding: 12 }}>
                <div style={{ fontWeight: 600, color: '#1677ff', marginBottom: 6 }}>辅助信息</div>
                <div>偏好承运商：{preferredCarrierText || '-'}</div>
                {insuranceInfo ? (
                  <div>
                    保险信息：
                    {insuranceRequired === true ? (
                      <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                        {insuranceInfo}
                      </span>
                    ) : (
                      <span>{insuranceInfo}</span>
                    )}
                  </div>
                ) : <div>保险信息：-</div>}
                {signatureInfo ? (
                  <div>
                    签名需求：
                    {signatureRequired === true ? (
                      <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                        {signatureInfo}
                      </span>
                    ) : (
                      <span>{signatureInfo}</span>
                    )}
                  </div>
                ) : <div>签名需求：-</div>}
                <div>买家备注：{buyerRemarksText || '-'}</div>
                <div>指令备注：{instructionRemarksText || '-'}</div>
                <div>操作提醒：{executionTipsText || '-'}</div>
              </div>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>上传运单信息和附件</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ marginBottom: 6 }}>运单所属承运商</div>
                    <Select
                      value={buyWaybillCarrier || undefined}
                      onChange={(v) => setBuyWaybillCarrier(String(v || ''))}
                      placeholder="请选择承运商"
                      options={buyWaybillProviderOptions}
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>物流跟踪号</div>
                    <Input value={buyWaybillTrackingNo} onChange={(e) => setBuyWaybillTrackingNo(e.target.value)} placeholder="请输入物流跟踪号" />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>运单费用金额</div>
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      value={buyWaybillChargeAmount ?? undefined}
                      onChange={(v) => setBuyWaybillChargeAmount(v == null ? null : Number(v))}
                      placeholder="可为0，表示仓库免费提供"
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>币种</div>
                    <Input value={buyWaybillChargeCurrency} onChange={(e) => setBuyWaybillChargeCurrency(String(e.target.value || '').toUpperCase())} placeholder="USD" />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ marginBottom: 6 }}>费用备注</div>
                  <Input.TextArea rows={2} value={buyWaybillChargeNote} onChange={(e) => setBuyWaybillChargeNote(e.target.value)} placeholder="可选" />
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ marginBottom: 6 }}>运单附件</div>
                  {waybillAttachments.length > 0 ? (
                    <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ color: '#666' }}>当前运单文件:</span>
                      {waybillAttachments.map((att: any, idx: number) => {
                        const fileName = String(att?.file_name || att?.name || att?.file || att?.file_path || `运单附件${idx + 1}`);
                        return (
                          <span key={`waybill-file-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span>{fileName}</span>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => { void openAttachmentPreview(att); }}
                            >
                              查看
                            </Button>
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                  <Upload
                    beforeUpload={() => false}
                    fileList={buyWaybillFileList}
                    onChange={(info) => setBuyWaybillFileList((info.fileList || []).slice(-1))}
                    onPreview={(file) => { void previewBuyWaybillUploadFile(file); }}
                    onRemove={() => {
                      setBuyWaybillFileList([]);
                      return true;
                    }}
                    itemRender={(originNode, file, _fileList, actions) => (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{ color: '#1677ff', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            actions.preview?.();
                          }}
                        >
                          {String((file as any)?.name || '运单附件')}
                        </span>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            actions.preview?.();
                          }}
                        >
                          查看
                        </Button>
                        {actions.remove ? (
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              actions.remove?.();
                            }}
                          >
                            移除
                          </Button>
                        ) : null}
                      </div>
                    )}
                    maxCount={1}
                    accept=".pdf,.png,.jpg,.jpeg"
                  >
                    <Button>选择文件</Button>
                  </Upload>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal
        title="分拣验证"
        open={pickVerifyOpen}
        onCancel={() => setPickVerifyOpen(false)}
        onOk={confirmPickVerify}
        okText="确认并保存"
        cancelText="取消"
        width={900}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <Input
            ref={pickScanInputRef}
            placeholder="请输入/扫码分拣条码或库存单元系统编码"
            value={pickScanInput}
            onChange={(e) => setPickScanInput(e.target.value)}
            onPressEnter={applyPickVerifyScan}
          />
          <Button type="primary" onClick={applyPickVerifyScan}>校验</Button>
        </div>
        <div style={{ marginBottom: 8, color: '#595959' }}>
          总数：{pickVerifyModalStats.total}，已分拣：{pickVerifyModalStats.done}，未分拣：{pickVerifyModalStats.pending}
        </div>
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={inventoryRows}
          locale={{ emptyText: '暂无库存单元信息' }}
          columns={[
            { title: '项目', dataIndex: 'project', key: 'project', width: 220 },
            { title: '库存单元', dataIndex: 'target_label', key: 'target_label', width: 220 },
            { title: '分拣条码', dataIndex: 'sorting_barcode', key: 'sorting_barcode' },
            { title: '目标数量', dataIndex: 'target_qty', key: 'target_qty', width: 120, align: 'center' as const },
            {
              title: '已分拣',
              key: 'pick_verified',
              width: 100,
              align: 'center' as const,
              render: (_: any, row: any) => getVerifiedQty(row, pickVerifyTempMap) || '',
            },
          ] as any}
          onRow={(row: any) => (isRowReached(row, pickVerifyTempMap) ? { style: { background: '#f6ffed' } } : {})}
        />
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, paddingRight: 36 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewTitle}</span>
            <span
              style={{
                color: previewPrintText ? '#ff4d4f' : '#999',
                fontSize: 14,
                fontWeight: 500,
                marginRight: 16,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {previewPrintText || '未配置打印设置'}
            </span>
          </div>
        )}
        open={previewOpen}
        onCancel={closePreview}
        footer={(
          <Space>
            <Button onClick={closePreview}>关闭</Button>
            <Button type="primary" onClick={printPreview}>打印</Button>
          </Space>
        )}
        width="75vw"
        destroyOnClose
      >
        {previewLoading ? (
          <div style={{ color: '#8c8c8c', marginBottom: 8 }}>附件加载中...</div>
        ) : null}
        <div style={{ height: '70vh' }}>
          {previewType === 'image' ? (
            <img src={previewUrl} alt={previewTitle} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <iframe ref={previewFrameRef} title="运单附件预览" src={previewUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SingleDropshipPackRenderer;

